import { Box, Text } from '@chakra-ui/react';
import { useMemo, useState, useEffect } from 'react';
import type { Column } from 'react-table';
import Datatable from '@/components/common/Datatable';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { formatApiDateToDisplay } from '@/utils/dateUtils';
import { useGetVendorPaymentHistory } from '@/services/payment';

interface Order {
  payment_id: string;
  payment_date: string;
  amount_paid: number;
  payment_method: string;
  reference: string;
  status: string;
  currency: string;
}

const AdminVendorPayment: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<{
    from_date?: string;
    to_date?: string;
  }>({});

  const pageSize = 10;

  // In AdminVendorPayment.tsx
  const { data, isLoading, isError, refetch } = useGetVendorPaymentHistory({
    page: currentPage,
    page_size: pageSize,
    q: query,
    start_date: filters.from_date,
    end_date: filters.to_date,
  });

  const [tableData, setTableData] = useState<Order[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize,
    totalCount: 0,
    totalPages: 1,
  });

  useEffect(() => {
    if (data?.results) {
      const transformed = data.results.map((item) => ({
        payment_id: item.transaction_id,
        payment_date: item.paid_date,
        amount_paid: parseFloat(item.amount_paid) || 0,
        payment_method: item.method.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        reference: item.ref_no,
        status: parseFloat(item.amount_paid) > 0 ? 'Paid' : 'Pending',
        currency: item.currency || 'USD',
      }));

      setTableData(transformed);
      setPagination((prev) => ({
        ...prev,
        pageSize: data.pageSize || pageSize,
        totalCount: data.count || 0,
        totalPages: Math.max(1, data.page_count || 1),
      }));

      if (data.page_count && currentPage > data.page_count && data.page_count > 0) {
        setCurrentPage(data.page_count);
      }
    }
  }, [data, currentPage]);

  const handleSearch = (value: string) => {
    setQuery(value);
    setCurrentPage(1);
    refetch();
  };

  const applyFilters = () => {
    setCurrentPage(1);
    refetch();
  };

  const resetFilters = () => {
    setFilters({});
    setCurrentPage(1);
    refetch();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns: Column<Order>[] = useMemo(
    () => [
      {
        Header: 'Payment ID',
        accessor: 'payment_id',
        Cell: ({ value }) => (
          <Text fontWeight="medium" color="text.primary">
            {value}
          </Text>
        ),
      },
      {
        Header: 'Payment Date',
        accessor: 'payment_date',
        Cell: ({ value }) => (
          <Text fontWeight="medium" color="text.primary">
            {formatApiDateToDisplay(value)}
          </Text>
        ),
      },
      {
        Header: 'Amount Paid',
        accessor: 'amount_paid',
        Cell: ({ row }) => {
          const currencySymbol = getCurrencySymbol(row.original.currency);
          return (
            <Text fontWeight="medium" color="text.primary">
              {currencySymbol}
              {row.original.amount_paid.toFixed(2)}
            </Text>
          );
        },
      },
      {
        Header: 'Reference',
        accessor: 'reference',
        Cell: ({ value }) => (
          <Text fontWeight="medium" color="text.primary">
            {value}
          </Text>
        ),
      },
    ],
    [],
  );

  return (
    <Box bg="background.white">
      <Datatable<Order>
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        isError={isError}
        onSearch={handleSearch}
        pagination={{
          page: currentPage,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        hideCategoryFilter={true}
        hideFilters={false}
        dateFilterType="start_end"
        restrictDateRange={true}
      />
    </Box>
  );
};

export default AdminVendorPayment;
